import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChatCircleDots, WarningCircle, CheckCircle, Copy } from "@phosphor-icons/react";

export default function WhatsAppTemplatesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState({});
  const [enabledStages, setEnabledStages] = useState([]);
  const { user } = { user: null };  // not needed; admin-only enforced server-side

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/registrations/templates/settings");
        setData(data);
        setTemplates(data.templates || {});
        setEnabledStages(data.enabled_stages || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/registrations/templates/settings", {
        templates,
        enabled_stages: enabledStages,
      });
      toast.success("WhatsApp templates saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleStage = (key) => {
    setEnabledStages((cur) => (cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]));
  };

  const copyPlaceholder = (p) => {
    navigator.clipboard?.writeText(p);
    toast.success(`Copied ${p}`);
  };

  if (loading) return null;
  if (!data) return null;

  return (
    <div className="border-2 border-ink" data-testid="whatsapp-templates-panel">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChatCircleDots size={18} weight="bold" />
          <h3 className="font-display text-base font-semibold">Customer WhatsApp Notifications</h3>
        </div>
        {data.twilio_configured ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-[#00B341] text-[#00713a]">
            <CheckCircle size={11} weight="bold" /> Live · {data.twilio_from}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-amber-500 text-amber-700">
            <WarningCircle size={11} weight="bold" /> Dry-Run (no Twilio creds)
          </span>
        )}
      </div>

      <div className="p-4 space-y-4 text-sm">
        {!data.twilio_configured && (
          <div className="border border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900 leading-relaxed">
            <strong>Dry-run mode active.</strong> Edit templates / toggle stages freely — messages are logged to <code className="font-mono">db.whatsapp_log</code> without actually being sent. To go live, set these env vars on the backend and redeploy:
            <ul className="mt-1 ml-4 list-disc font-mono text-[11px]">
              <li>TWILIO_ACCOUNT_SID</li>
              <li>TWILIO_AUTH_TOKEN</li>
              <li>TWILIO_WHATSAPP_FROM (e.g. whatsapp:+14155238886 for sandbox)</li>
            </ul>
          </div>
        )}

        {/* Placeholders */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Available Placeholders (click to copy)
          </div>
          <div className="flex flex-wrap gap-1">
            {data.available_placeholders.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => copyPlaceholder(p)}
                className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 border border-canvas-line hover:border-ink"
              >
                <Copy size={9} weight="bold" />
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Per-stage template editor */}
        <div className="space-y-3">
          {[...data.stage_specs].sort((a, b) => (a.phase === "post_sale") - (b.phase === "post_sale")).map((spec, i, arr) => {
            const enabled = enabledStages.includes(spec.key);
            const newSection = i === 0 || (arr[i - 1].phase === "post_sale") !== (spec.phase === "post_sale");
            return (
              <div key={spec.key}>
              {newSection && (
                <p className="font-bold text-sm uppercase tracking-wide pt-2" data-testid={`wa-section-${spec.phase === "post_sale" ? "post-sale" : "rto"}`}>
                  {spec.phase === "post_sale" ? "Post-Sale team messages" : "Registrations (RTO) team messages"}
                </p>
              )}
              <div
                className={`border ${enabled ? "border-ink" : "border-canvas-line"} p-3 space-y-2`}
                data-testid={`wa-stage-${spec.key}`}
              >
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => toggleStage(spec.key)}
                      data-testid={`wa-enable-${spec.key}`}
                    />
                    <span className="font-display font-semibold">{spec.label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                      SLA {spec.sla_days}d
                    </span>
                  </label>
                  {enabled && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-[#00713a]">
                      sends on completion
                    </span>
                  )}
                </div>
                <textarea
                  value={templates[spec.key] || ""}
                  onChange={(e) => setTemplates({ ...templates, [spec.key]: e.target.value })}
                  rows={3}
                  data-testid={`wa-template-${spec.key}`}
                  className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink disabled:opacity-50"
                  placeholder="e.g. Hi {customer_name}, your {vehicle_model} is ready at {dealership_name}."
                  disabled={!enabled}
                />
              </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="wa-save-btn"
            className="px-4 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Templates"}
          </button>
        </div>
      </div>
    </div>
  );
}
